import { ApolloQueryResult, useApolloClient } from "@apollo/client";
import { USE_BACKEND_SEARCH } from "@dashboard/config";
import {
  ProductListDocument,
  ProductListQuery,
  ProductListQueryVariables,
  ProductOrderField,
  ProductSearchDocument,
  ProductSearchQuery,
  ProductSearchQueryVariables,
} from "@dashboard/graphql";
import { Pagination, RelayToFlat } from "@dashboard/types";
import omit from "lodash/omit";
import { useEffect, useMemo, useRef, useState } from "react";

import { decodeOffsetCursor, encodeOffsetCursor, productMatchesSearch } from "./clientSearch";

type ProductNode = NonNullable<RelayToFlat<ProductListQuery["products"]>>[number];

// Saleor caps `first` at 100; 10 pages keeps the whole fetch well above the
// expected catalog size while bounding the work for large stores.
const PAGE_SIZE = 100;
const MAX_PAGES = 10;
// Backend `productSearch` returns the whole ranked match set in one page (the
// resolver raises its cap to MEILISEARCH_MAX_RESULTS, currently 200).
const BACKEND_FIRST = 200;

interface UseClientProductSearchArgs {
  enabled: boolean;
  /** Base list variables (where/channel/sort/include flags). Pagination and search are ignored. */
  variables: ProductListQueryVariables;
  rowNumber: number;
  params: Pagination;
  terms: string[];
  /** Raw (un-tokenized) query string — sent to the backend search when enabled. */
  rawQuery: string;
}

interface UseClientProductSearchResult {
  data: ProductListQuery | undefined;
  loading: boolean;
  refetch: () => void;
}

/**
 * Resolves the product list search client-side, mirroring the storefront: fetch
 * the whole catalog matching the active (non-search) filters once, then filter
 * and paginate in memory. This gives accent- and case-insensitive substring
 * matching that Saleor's native full-text `search` argument cannot provide.
 */
export function useClientProductSearch({
  enabled,
  variables,
  rowNumber,
  params,
  terms,
  rawQuery,
}: UseClientProductSearchArgs): UseClientProductSearchResult {
  const client = useApolloClient();
  const [allNodes, setAllNodes] = useState<ProductNode[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  // When on, the backend `productSearch` resolver ranks and filters; otherwise we
  // fetch the whole catalog and match in the browser.
  const backend = USE_BACKEND_SEARCH;

  // Only the filter-defining variables drive a refetch — typing (terms) and
  // paging (params) are resolved in memory without hitting the network.
  const baseVariables = omit(variables, ["after", "before", "first", "last", "search"]);

  // RANK ordering requires Saleor's `search` argument, which the client-side
  // search intentionally omits. Drop it so the catalog fetch doesn't error.
  if (
    baseVariables.sort &&
    "field" in baseVariables.sort &&
    baseVariables.sort.field === ProductOrderField.RANK
  ) {
    baseVariables.sort = undefined;
  }

  // In backend mode the query string is sent to the server, so it must be part
  // of the refetch key; in client mode typing is resolved in memory (terms).
  const fetchKey = JSON.stringify(backend ? { ...baseVariables, q: rawQuery } : baseVariables);
  const requestIdRef = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = () => {
    // Drop the cached catalog so the next fetch reads fresh data (e.g. after a
    // bulk delete) instead of serving the stale cache-first result. Evict both
    // fields so it works in either search mode.
    client.cache.evict({ fieldName: "products" });
    client.cache.evict({ fieldName: "productSearch" });
    client.cache.gc();
    setReloadToken(token => token + 1);
  };

  useEffect(() => {
    if (!enabled) {
      setAllNodes(undefined);
      setLoading(false);

      return;
    }

    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;
    setLoading(true);

    const fetchAll = async () => {
      // Backend path: one call to `productSearch` returns the whole ranked match
      // set (accent/separator folding, typo tolerance, synonyms handled server
      // side). No in-memory term filtering afterwards — Meili already decided.
      if (backend) {
        const result = await client.query<ProductSearchQuery, ProductSearchQueryVariables>({
          query: ProductSearchDocument,
          variables: {
            query: rawQuery,
            first: BACKEND_FIRST,
            channel: variables.channel,
            hasChannel: variables.hasChannel,
            includeCategories: variables.includeCategories,
            includeCollections: variables.includeCollections,
          },
          fetchPolicy: "cache-first",
        });
        const nodes = (result.data?.productSearch?.edges ?? []).map(
          edge => edge.node,
        ) as unknown as ProductNode[];

        if (requestIdRef.current === requestId) {
          setAllNodes(nodes);
          setLoading(false);
        }

        return;
      }

      const collected: ProductNode[] = [];
      let cursor: string | null = null;

      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result: ApolloQueryResult<ProductListQuery> = await client.query<
          ProductListQuery,
          ProductListQueryVariables
        >({
          query: ProductListDocument,
          variables: { ...baseVariables, first: PAGE_SIZE, after: cursor },
          fetchPolicy: "cache-first",
        });

        const connection = result.data?.products;

        if (!connection) {
          break;
        }

        collected.push(...connection.edges.map(edge => edge.node));

        if (!connection.pageInfo.hasNextPage) {
          break;
        }

        cursor = connection.pageInfo.endCursor;
      }

      // Ignore results from a superseded request (filters changed mid-flight).
      if (requestIdRef.current === requestId) {
        setAllNodes(collected);
        setLoading(false);
      }
    };

    fetchAll().catch(() => {
      if (requestIdRef.current === requestId) {
        setAllNodes([]);
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchKey, reloadToken]);

  const data = useMemo<ProductListQuery | undefined>(() => {
    if (!enabled || allNodes === undefined) {
      return undefined;
    }

    // Backend already filtered and ranked; client mode matches in memory.
    const matched = backend ? allNodes : allNodes.filter(node => productMatchesSearch(node, terms));
    const total = matched.length;

    // Resolve the in-memory page offset from the synthetic cursors. `after`
    // carries the next page's start offset; `before` carries the offset of the
    // page we navigated back from.
    const afterOffset = decodeOffsetCursor(params.after);
    const beforeOffset = decodeOffsetCursor(params.before);

    let offset = 0;

    if (afterOffset !== null) {
      offset = afterOffset;
    } else if (beforeOffset !== null) {
      offset = Math.max(0, beforeOffset - rowNumber);
    }

    if (offset >= total) {
      offset = 0;
    }

    const pageNodes = matched.slice(offset, offset + rowNumber);

    return {
      __typename: "Query",
      products: {
        __typename: "ProductCountableConnection",
        totalCount: total,
        edges: pageNodes.map(node => ({
          __typename: "ProductCountableEdge" as const,
          node,
        })),
        pageInfo: {
          __typename: "PageInfo" as const,
          startCursor: encodeOffsetCursor(offset),
          endCursor: encodeOffsetCursor(offset + pageNodes.length),
          hasNextPage: offset + pageNodes.length < total,
          hasPreviousPage: offset > 0,
        },
      },
    };
  }, [enabled, allNodes, terms, params.after, params.before, rowNumber]);

  return { data, loading: enabled && (loading || allNodes === undefined), refetch };
}
